/**
 * SMS inbox (/messages) — two-way conversations, READ side only (plan §9.4).
 *
 * Shown only when sms_conversations_available() is true (route gate + nav). Polls every 60 seconds
 * (Q6: polling, not realtime). "Last updated" is the time of the last SUCCESSFUL refresh, so a
 * failing poll never makes stale rows look current.
 *
 * Not built here because the server does not provide it yet: the Open/Closed and Reply unconfirmed
 * filters, owner names, "Messages synced" (projection freshness), and the admin queues.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import {
  listSmsConversations,
  type SmsConversationError,
  type SmsConversationRow,
  type SmsConversationTab,
} from "../services/smsConversations.service";
import { formatSmsTime } from "../utils/smsConversationFormat";
import SharedNumberChip from "../components/sms/SharedNumberChip";

const POLL_MS = 60_000;

const TABS: Array<{ id: SmsConversationTab; label: string }> = [
  { id: "mine", label: "Mine" },
  { id: "unassigned", label: "Unassigned" },
  { id: "all", label: "All" },
];

const Badge: React.FC<{ children: React.ReactNode; tone: string }> = ({
  children,
  tone,
}) => (
  <span
    className={clsx(
      "inline-flex items-center rounded border px-1.5 py-0.5 text-xs font-medium",
      tone,
    )}
  >
    {children}
  </span>
);

const Row: React.FC<{ row: SmsConversationRow }> = ({ row }) => (
  <li>
    <Link
      to={`/messages/${row.id}`}
      className="block px-4 py-3 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={clsx(
                "font-mono text-sm text-gray-900",
                (row.unread || row.reminder) && "font-semibold",
              )}
            >
              {row.member_masked}
            </span>
            <SharedNumberChip count={row.associations} />
          </div>
          {row.context_name && (
            <div className="mt-0.5 truncate text-sm text-gray-700">
              {row.context_name}
            </div>
          )}
          <div className="mt-1 flex flex-wrap gap-1.5">
            {row.unread && (
              <Badge tone="border-blue-300 bg-blue-50 text-blue-800">
                Unread
              </Badge>
            )}
            {row.reminder && (
              <Badge tone="border-purple-300 bg-purple-50 text-purple-800">
                Reminder
              </Badge>
            )}
            {row.needs_reply && (
              <Badge tone="border-red-300 bg-red-50 text-red-800">
                Needs reply
              </Badge>
            )}
            {row.status === "closed" && (
              <Badge tone="border-gray-300 bg-gray-50 text-gray-700">
                Closed
              </Badge>
            )}
          </div>
        </div>
        <div className="shrink-0 text-xs text-gray-500">
          {formatSmsTime(row.last_item_at)}
        </div>
      </div>
    </Link>
  </li>
);

const SmsInbox: React.FC = () => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<SmsConversationTab>("all");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [needsReplyOnly, setNeedsReplyOnly] = useState(false);

  const q = useQuery({
    queryKey: ["sms-conversation-list", tab],
    queryFn: () => listSmsConversations(tab, 200),
    refetchInterval: POLL_MS,
    retry: false,
  });

  const rows = useMemo(
    () =>
      (q.data ?? []).filter(
        (r) =>
          (!unreadOnly || r.unread || r.reminder) &&
          (!needsReplyOnly || r.needs_reply),
      ),
    [q.data, unreadOnly, needsReplyOnly],
  );

  const err = q.error as SmsConversationError | null;
  // The server withdrew access since the probe: treat as unavailable.
  const withdrawn = err?.kind === "disabled" || err?.kind === "not_authorized";
  useEffect(() => {
    if (withdrawn) {
      void queryClient.invalidateQueries({
        queryKey: ["sms-conversations-available"],
      });
    }
  }, [withdrawn, queryClient]);
  if (withdrawn) {
    return <Navigate to="/dashboard" replace />;
  }

  let body: React.ReactNode;
  if (q.isLoading) {
    body = <p className="p-6 text-sm text-gray-600">Loading conversations…</p>;
  } else if (q.isError && !q.data) {
    body = (
      <div className="p-6 text-sm">
        <p className="text-red-800">Conversations couldn't load.</p>
        <button
          type="button"
          onClick={() => void q.refetch()}
          className="mt-2 rounded border border-gray-300 px-3 py-1 text-sm text-gray-800 hover:bg-gray-50"
        >
          Retry
        </button>
      </div>
    );
  } else if (rows.length === 0) {
    body = <p className="p-6 text-sm text-gray-600">No conversations.</p>;
  } else {
    body = (
      <ul className="divide-y divide-gray-200">
        {rows.map((r) => (
          <Row key={r.id} row={r} />
        ))}
      </ul>
    );
  }

  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6">
      <h1 className="text-2xl font-semibold text-gray-900">SMS inbox</h1>

      <div
        role="tablist"
        aria-label="Conversations"
        className="mt-4 flex gap-6 border-b border-gray-200"
      >
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              "-mb-px border-b-2 px-1 py-3 text-sm font-medium",
              tab === t.id
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <fieldset className="flex flex-wrap gap-4">
          <legend className="sr-only">Filters</legend>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={(e) => setUnreadOnly(e.target.checked)}
            />
            Unread by me
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={needsReplyOnly}
              onChange={(e) => setNeedsReplyOnly(e.target.checked)}
            />
            Needs reply
          </label>
        </fieldset>
        <div className="text-xs text-gray-500" aria-live="off">
          {q.dataUpdatedAt > 0
            ? `Last updated ${formatSmsTime(new Date(q.dataUpdatedAt).toISOString())}`
            : null}
        </div>
      </div>
      {needsReplyOnly && (
        <p className="mt-2 text-xs text-gray-600">
          Needs reply reflects unanswered messages you can access. A colleague's
          reply may not cover every message visible to you.
        </p>
      )}

      <div
        role="tabpanel"
        className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white"
      >
        {body}
        {q.isError && q.data && (
          <div className="border-t border-gray-200 p-3 text-xs text-red-800">
            Conversations couldn't load.{" "}
            <button
              type="button"
              onClick={() => void q.refetch()}
              className="underline"
            >
              Retry
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SmsInbox;
